import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { TrendingUp, Activity, Target, Users as UsersIcon, ChevronRight } from 'lucide-react';

// ============================================
// TRADER ANALYTICS COMPONENT
// Displays comprehensive trader leaderboard data
// ============================================

interface TraderStats {
  userId: number;
  username: string | null;
  totalPnL: number;
  totalTrades: number;
  wins: number;
  losses: number;
  winRate: number;
  groupsCount: number;
}

interface GroupTraderStats {
  chatId: number;
  groupName: string;
  totalTraders: number;
  topTraders: TraderStats[];
}

interface ComprehensiveTraderData {
  topTradersByPnL: TraderStats[];
  mostActiveTraders: TraderStats[];
  topTradersByWinRate: TraderStats[];
  topTradersPerGroup: GroupTraderStats[];
  summary: {
    totalTraders: number;
    totalGroups: number;
    groupsWithTraders: number;
  };
}

// Container for the analytics section
const AnalyticsContainer = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 485px;
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 300px;
  }
`;

const AnalyticsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const AnalyticsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  
  svg {
    width: 16px;
    height: 16px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SummaryStats = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  gap: 24px;
  font-size: 12px;
`;

const SummaryStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
`;

const SummaryValue = styled.div`
  color: #10b981;
  font-weight: 600;
  font-size: 14px;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.05)' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#10b981' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }
  
  svg {
    width: 14px;
    height: 14px;
    margin-right: 6px;
    vertical-align: middle;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const TraderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  background: rgba(20, 20, 25, 0.95);
  z-index: 1;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const TableHeaderCell = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:first-child {
    padding-left: 16px;
  }
  
  &:last-child {
    padding-right: 16px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 10px 12px;
  color: rgba(255, 255, 255, 0.8);
  
  &:first-child {
    padding-left: 16px;
  }
  
  &:last-child {
    padding-right: 16px;
  }
`;

const RankCell = styled(TableCell)`
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  width: 40px;
`;

const UsernameCell = styled(TableCell)`
  font-weight: 600;
  color: #ffffff;
`;

const PnLCell = styled(TableCell)<{ $positive: boolean }>`
  font-weight: 600;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
`;

const WinRateCell = styled(TableCell)<{ $rate: number }>`
  color: ${props => {
    if (props.$rate >= 60) return '#10b981';
    if (props.$rate >= 40) return '#f59e0b';
    return '#ef4444';
  }};
  font-weight: 500;
`;

const GroupSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const GroupHeader = styled.div`
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-weight: 600;
  color: #ffffff;
  font-size: 13px;
`;

const LoadingState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

const ErrorState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #ef4444;
  font-size: 13px;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

// Format currency with +/- sign
const formatPnL = (pnl: number): string => {
  const sign = pnl >= 0 ? '+' : '';
  return `${sign}$${pnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format win rate as percentage
const formatWinRate = (rate: number): string => {
  return `${rate.toFixed(1)}%`;
};

// Format username
const formatUsername = (username: string | null, userId: number): string => {
  if (username) return `@${username}`;
  return `User ${userId}`;
};

// Get rank emoji
const getRankEmoji = (rank: number): string => {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `${rank}.`;
};

// Fetch function
const fetchTraderAnalytics = async (limit = 10): Promise<ComprehensiveTraderData> => {
  // Check if we have the Sentry Bot API URL configured
  const apiUrl = (import.meta.env as any).VITE_SENTRY_BOT_API_URL;
  if (!apiUrl) {
    throw new Error('Sentry Bot API URL not configured');
  }
  
  const response = await fetch(`${apiUrl}/api/leaderboard/traders/comprehensive?limit=${limit}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to fetch trader analytics' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  
  // Handle wrapped response
  if (data.success && data.data) {
    return data.data;
  }
  
  // Handle direct response
  return data;
};

type TabType = 'pnl' | 'active' | 'winrate' | 'groups';

const TraderAnalytics: React.FC = () => {
  const [data, setData] = useState<ComprehensiveTraderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('pnl');
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchTraderAnalytics(10);
        setData(result);
      } catch (err: any) {
        console.error('[TraderAnalytics] Error fetching data:', err);
        setError(err.message || 'Failed to load trader analytics');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  const renderTraderTable = (traders: TraderStats[]) => {
    if (traders.length === 0) {
      return <EmptyState>No traders found</EmptyState>;
    }
    
    return (
      <TraderTable>
        <TableHeader>
          <TableHeaderRow>
            <TableHeaderCell>Rank</TableHeaderCell>
            <TableHeaderCell>Username</TableHeaderCell>
            <TableHeaderCell>Total PnL</TableHeaderCell>
            <TableHeaderCell>Trades</TableHeaderCell>
            <TableHeaderCell>Win Rate</TableHeaderCell>
            <TableHeaderCell>Groups</TableHeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {traders.map((trader, index) => (
            <TableRow key={trader.userId}>
              <RankCell>{getRankEmoji(index + 1)}</RankCell>
              <UsernameCell>{formatUsername(trader.username, trader.userId)}</UsernameCell>
              <PnLCell $positive={trader.totalPnL >= 0}>
                {formatPnL(trader.totalPnL)}
              </PnLCell>
              <TableCell>{trader.totalTrades}</TableCell>
              <WinRateCell $rate={trader.winRate}>
                {formatWinRate(trader.winRate)}
              </WinRateCell>
              <TableCell>{trader.groupsCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TraderTable>
    );
  };
  
  const renderGroupsTable = () => {
    if (!data || data.topTradersPerGroup.length === 0) {
      return <EmptyState>No group data found</EmptyState>;
    }
    
    return (
      <div>
        {data.topTradersPerGroup.map((group) => (
          <GroupSection key={group.chatId}>
            <GroupHeader>{group.groupName} ({group.totalTraders} traders)</GroupHeader>
            <TraderTable>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Rank</TableHeaderCell>
                  <TableHeaderCell>Username</TableHeaderCell>
                  <TableHeaderCell>PnL (Group)</TableHeaderCell>
                  <TableHeaderCell>Trades</TableHeaderCell>
                  <TableHeaderCell>Win Rate</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {group.topTraders.map((trader, index) => (
                  <TableRow key={trader.userId}>
                    <RankCell>{getRankEmoji(index + 1)}</RankCell>
                    <UsernameCell>{formatUsername(trader.username, trader.userId)}</UsernameCell>
                    <PnLCell $positive={trader.totalPnL >= 0}>
                      {formatPnL(trader.totalPnL)}
                    </PnLCell>
                    <TableCell>{trader.totalTrades}</TableCell>
                    <WinRateCell $rate={trader.winRate}>
                      {formatWinRate(trader.winRate)}
                    </WinRateCell>
                  </TableRow>
                ))}
              </TableBody>
            </TraderTable>
          </GroupSection>
        ))}
      </div>
    );
  };
  
  const renderContent = () => {
    if (loading) {
      return <LoadingState>Loading trader analytics...</LoadingState>;
    }
    
    if (error) {
      return <ErrorState>Error: {error}</ErrorState>;
    }
    
    if (!data) {
      return <EmptyState>No data available</EmptyState>;
    }
    
    switch (activeTab) {
      case 'pnl':
        return renderTraderTable(data.topTradersByPnL);
      case 'active':
        return renderTraderTable(data.mostActiveTraders);
      case 'winrate':
        return renderTraderTable(data.topTradersByWinRate);
      case 'groups':
        return renderGroupsTable();
      default:
        return renderTraderTable(data.topTradersByPnL);
    }
  };
  
  return (
    <AnalyticsContainer>
      <AnalyticsHeader>
        <AnalyticsTitle>
          <TrendingUp /> Trader Analytics
        </AnalyticsTitle>
        <ExpandButton onClick={() => setExpanded(!expanded)}>
          <ChevronRight style={{ transform: expanded ? 'rotate(90deg)' : 'none' }} />
        </ExpandButton>
      </AnalyticsHeader>
      
      {data && !expanded && (
        <SummaryStats>
          <SummaryStat>
            <SummaryLabel>Total Traders</SummaryLabel>
            <SummaryValue>{data.summary.totalTraders}</SummaryValue>
          </SummaryStat>
          <SummaryStat>
            <SummaryLabel>Total Groups</SummaryLabel>
            <SummaryValue>{data.summary.totalGroups}</SummaryValue>
          </SummaryStat>
          <SummaryStat>
            <SummaryLabel>Active Groups</SummaryLabel>
            <SummaryValue>{data.summary.groupsWithTraders}</SummaryValue>
          </SummaryStat>
        </SummaryStats>
      )}
      
      {expanded && (
        <>
          <TabsContainer>
            <Tab $active={activeTab === 'pnl'} onClick={() => setActiveTab('pnl')}>
              <TrendingUp /> Top by PnL
            </Tab>
            <Tab $active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
              <Activity /> Most Active
            </Tab>
            <Tab $active={activeTab === 'winrate'} onClick={() => setActiveTab('winrate')}>
              <Target /> Best Win Rate
            </Tab>
            <Tab $active={activeTab === 'groups'} onClick={() => setActiveTab('groups')}>
              <UsersIcon /> By Group
            </Tab>
          </TabsContainer>
          <ContentContainer>
            {renderContent()}
          </ContentContainer>
        </>
      )}
      
      {!expanded && (
        <ContentContainer>
          {loading ? (
            <LoadingState>Loading...</LoadingState>
          ) : error ? (
            <ErrorState>Error: {error}</ErrorState>
          ) : data ? (
            renderTraderTable(data.topTradersByPnL.slice(0, 5))
          ) : (
            <EmptyState>No data available</EmptyState>
          )}
        </ContentContainer>
      )}
    </AnalyticsContainer>
  );
};

export default TraderAnalytics;